/* ==========================================================================
   CARTER FARRAGO — interactions
   Carousels, spec tabs, FAQ accordion, scroll reveal, video play layers.
   Every widget is opt-in via a data-attribute so the sections share one file.

   Shopify note: init() is idempotent (each widget marks its root with
   data-cf-init) and takes a scope, so the theme editor can re-initialise a
   single section after Shopify replaces its DOM without double-binding the
   widgets in sections that did not change.
   ========================================================================== */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  /* In an embed/preview the frame may never intersect the top-level viewport,
     so the reveal observer would never fire and sections would stay blank. */
  var embedded = window.self !== window.top;
  var editing = !!(window.Shopify && window.Shopify.designMode);

  var io = null;
  function observer() {
    if (io) return io;
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });
    return io;
  }

  /* ------------------------------------------------------------ reveal -- */
  function initReveal(scope) {
    var revealables = scope.querySelectorAll('.rv:not([data-cf-rv])');
    if (!revealables.length) return;
    /* In the editor a section can be re-rendered while off-screen; showing it
       straight away avoids a blank section the merchant cannot preview. */
    var instant = !('IntersectionObserver' in window) || reduced || embedded || editing;
    revealables.forEach(function (el) {
      el.setAttribute('data-cf-rv', '1');
      if (instant) el.classList.add('in');
      else observer().observe(el);
    });
  }

  /* ----------------------------------------------------------- stagger -- */
  /* Write an increasing transition-delay onto each .rv inside the container so
     a grid or list arrives in sequence. Done here rather than in CSS because
     the number of items is set by the merchant, not known to the stylesheet. */
  function initStagger(scope) {
    scope.querySelectorAll('[data-cf-stagger]:not([data-cf-stag])').forEach(function (root) {
      root.setAttribute('data-cf-stag', '1');
      var step = parseInt(root.getAttribute('data-cf-stagger'), 10);
      if (!step) {
        step = parseInt(getComputedStyle(root).getPropertyValue('--cf-step'), 10) || 90;
      }
      var items = root.querySelectorAll('.rv');
      /* Cap the run so a long list does not leave the last item waiting. */
      var cap = 8;
      items.forEach(function (el, i) {
        el.style.transitionDelay = (Math.min(i, cap) * step) + 'ms';
      });
    });
  }

  /* --------------------------------------------------------- carousels -- */
  /* A carousel is any [data-carousel] with a track, slides and (optionally)
     prev/next buttons plus a progress bar. `perView` is read back from the
     rendered layout so the CSS stays the single source of truth. */
  function initCarousels(scope) {
    scope.querySelectorAll('[data-carousel]:not([data-cf-init])').forEach(function (root) {
      var track = root.querySelector('[data-track]');
      if (!track) return;
      var slides = Array.prototype.slice.call(track.children);
      if (!slides.length) return;
      root.setAttribute('data-cf-init', '1');

      var prev = root.querySelector('[data-prev]');
      var next = root.querySelector('[data-next]');
      var prog = root.querySelector('[data-prog]');
      var centred = root.hasAttribute('data-centred');
      var wrap = root.hasAttribute('data-cf-wrap');
      var promoteCentre = root.hasAttribute('data-cf-promote-centre');
      /* Which class marks the promoted card. cf-offices carries the attribute
         bare and wants .ocard--active, so that stays the default; anything else
         names its own class in the attribute value. */
      var promoteClass = root.getAttribute('data-cf-promote-centre') || 'ocard--active';
      var index = parseInt(root.getAttribute('data-start') || '0', 10);

      var real = slides.length;
      var pad = 0;                      /* clones added to each end */

      /* offsetLeft, not getBoundingClientRect: a rect includes any transform on
         the element, so a rail that scales its inactive cards down measures its
         own step wrong and every position after the first drifts. offsetLeft is
         the untransformed layout position, and the difference between two of
         them is the step whatever the track's own transform is doing. */
      function step() {
        if (slides.length < 2) return slides[0].offsetWidth;
        return slides[1].offsetLeft - slides[0].offsetLeft;
      }

      function perView() {
        var s = step();
        return s > 0 ? Math.max(1, Math.round(root.clientWidth / s)) : 1;
      }

      function maxIndex() {
        return centred ? slides.length - 1 : Math.max(0, slides.length - perView());
      }

      /* Endless mode. Slides are cloned onto both ends, so stepping off either
         edge lands on copies identical to the run on the far side; once that
         transition finishes we jump to the real ones with the transition
         suppressed. Going back from the first slide therefore brings the last
         in from the left, with no rewind.

         A rail showing three cards at a time has to clone three at each end,
         not one, or stepping back past the first would reveal blank track —
         hence measuring perView first and cloning that many. */
      function markClone(node) {
        node.setAttribute('aria-hidden', 'true');
        node.querySelectorAll('a,button,input,select,textarea').forEach(function (el) {
          el.tabIndex = -1;
        });
        node.classList.add('in');
      }

      /* How many copies to hang off each end.
       *
       * A centred rail used to clone exactly one, which is enough to step off
       * either edge but not enough to LOOK endless: the active card sits in the
       * middle of the viewport, so half a screen of cards is needed on each side
       * of it and one card cannot cover that. The run simply stopped, leaving
       * empty ground either side of it.
       *
       * So: half a viewport, plus one to spare. A left-aligned rail still only
       * needs one screen's worth ahead of it, which is what perView is. */
      function setupWrap() {
        if (!wrap || real < 2) return;
        pad = Math.max(1, centred ? Math.ceil(perView() / 2) + 1 : perView());

        var head = document.createDocumentFragment();
        var tail = document.createDocumentFragment();
        for (var i = 0; i < pad; i++) {
          /* Indexed modulo the real run, so pad may exceed it -- a rail of three
             cards in a wide viewport needs more clones than it has cards, and
             without the modulo that read past the ends of the array. */
          var h = slides[((real - pad + i) % real + real) % real].cloneNode(true);
          var t = slides[i % real].cloneNode(true);
          markClone(h); markClone(t);
          head.appendChild(h);
          tail.appendChild(t);
        }
        track.insertBefore(head, slides[0]);
        track.appendChild(tail);
        slides = Array.prototype.slice.call(track.children);
        index += pad;                   /* shift past the leading clones */
      }
      setupWrap();

      /* Where the track sits for a given index. Split out of apply() because the
         drag below needs the same number as its starting point -- two copies of
         this sum would drift the moment the centred case changed. */
      function offsetFor(i) {
        if (centred) {
          /* keep the active slide horizontally centred in the viewport */
          return i * step() - (root.clientWidth - slides[i].offsetWidth) / 2;
        }
        return i * step();
      }

      function apply(animate) {
        if (pad > 0) {
          /* clones included: real slides live at pad .. pad+real-1 */
          index = Math.max(0, Math.min(index, real + 2 * pad - 1));
        } else {
          index = Math.max(0, Math.min(index, maxIndex()));
        }
        /* The silent jump at the seam. The track is not the only thing that has to
           hold still for it: promoteCentre below moves .vcard--active from the clone
           to the real card, and those two fade over --glide-dur. Left running, the
           outgoing card dips to .55 while the incoming one climbs from it, which is
           a crossfade flicker at the exact moment nothing should appear to happen. */
        if (animate === false) {
          track.style.transition = 'none';
          root.classList.add('is-jumping');
        }
        track.style.transform = 'translate3d(' + (-offsetFor(index)) + 'px,0,0)';

        if (animate === false) {
          void track.offsetWidth;
          track.style.transition = '';
          root.classList.remove('is-jumping');
        }

        /* Endless mode never runs out of slides, so the controls never
           disable — that is the point of it. */
        if (prev) prev.disabled = wrap ? false : index <= 0;
        if (next) next.disabled = wrap ? false : index >= maxIndex();

        /* Promote whichever card sits in the middle. Which card that IS depends
           on how the rail is positioned, and the two cases are not the same:

             - a plain rail scrolls so that slides[index] is the LEFTMOST card,
               so the middle of the visible run is half a screen further along
             - a centred rail translates so that slides[index] is itself the
               middle card, and adding half a screen to that points at nothing
               in particular -- which is why the highlight was landing several
               cards to the right of centre, and off the end of the run.
        */
        if (promoteCentre) {
          var mid = centred ? index : index + Math.floor(perView() / 2);
          slides.forEach(function (sl, i) {
            sl.classList.toggle(promoteClass, i === mid);
          });
        }

        if (prog) {
          var total = pad > 0 ? real : maxIndex() + 1;
          prog.style.width = (100 / total) + '%';
          var slot = pad > 0 ? (index - pad + real) % real : index;
          prog.style.transform = 'translateX(' + (slot * 100) + '%)';
        }

        /* The rail announces where it has arrived, rather than every feature that
           cares reaching into it. The films in the collector rail need to know
           which slide is live so only that one plays; anything else that wants the
           same can listen without another copy of the index arithmetic. */
        root.dispatchEvent(new CustomEvent('cf:slide', {
          detail: {
            index: index,
            active: slides[centred ? index : index + Math.floor(perView() / 2)] || null,
            slides: slides
          }
        }));
      }

      /* ------------------------------------------------------ autoplay ----
       * Steps the rail on its own. Opt-in per section via data-cf-autoplay, in ms.
       *
       * It yields rather than competing: paused while the pointer is over the rail,
       * while the tab is hidden, and while the section is off screen -- a rail
       * advancing behind the visitor's back is only wasted work, and one advancing
       * under their cursor takes the card out from under them mid-reach. Any manual
       * move restarts the clock, so a click is never immediately overridden.
       */
      var autoMs = parseInt(root.getAttribute('data-cf-autoplay') || '0', 10);
      if (autoMs > 0 && !reduced) {
        var timer = null;
        var held = 0;                  /* bitfield of reasons to stay stopped */
        var HOVER = 1, HIDDEN = 2, OFFSCREEN = 4;

        function tick() {
          if (wrap) index += 1;
          else index = index >= maxIndex() ? 0 : index + 1;
          apply();
        }
        function run() {
          stop();
          if (held) return;
          timer = setInterval(tick, autoMs);
        }
        function stop() { if (timer) { clearInterval(timer); timer = null; } }
        function hold(bit, on) {
          held = on ? (held | bit) : (held & ~bit);
          if (held) stop(); else run();
        }

        root.addEventListener('pointerenter', function () { hold(HOVER, true); });
        root.addEventListener('pointerleave', function () { hold(HOVER, false); });
        root.addEventListener('focusin', function () { hold(HOVER, true); });
        document.addEventListener('visibilitychange', function () {
          hold(HIDDEN, document.hidden);
        });
        if ('IntersectionObserver' in window) {
          new IntersectionObserver(function (entries) {
            hold(OFFSCREEN, !entries[0].isIntersecting);
          }, { threshold: 0.15 }).observe(root);
        }
        /* Restart on any deliberate move, so the visitor's step gets its full
           dwell rather than whatever was left of the previous one. */
        [prev, next].forEach(function (b) {
          if (b) b.addEventListener('click', function () { if (!held) run(); });
        });
        track.addEventListener('pointerup', function () { if (!held) run(); });

        run();
      }

      if (pad > 0) {
        track.addEventListener('transitionend', function (e) {
          /* e.target, and this is the whole reason the wrap juddered.
           *
           * transitionend bubbles. The cards inside this track transition their own
           * transform -- the centred rail scales its inactive watches to .94 -- so
           * every time the active card changed, a child's transform transition
           * arrived here looking exactly like the track's own. The normalisation
           * below then ran mid-travel, jumping the rail a whole run sideways while
           * it was still moving. Worst at the seam, where it is the difference
           * between a silent swap and a visible lurch. */
          if (e.target !== track) return;
          if (e.propertyName !== 'transform') return;
          if (index < pad) { index += real; apply(false); }
          else if (index >= pad + real) { index -= real; apply(false); }
        });
      }

      if (prev) prev.addEventListener('click', function () { index -= 1; apply(); });
      if (next) next.addEventListener('click', function () { index += 1; apply(); });

      /* ---------------------------------------------------------- drag ----
       * The rail follows the finger and settles on release.
       *
       * This replaces a version that only read clientX on pointerup and stepped
       * one card if the total was over 48px. That was invisible while it was
       * happening -- nothing moved until you let go -- and it could not tell a
       * flick from a long haul across four cards.
       *
       * Axis is decided once, on the first few pixels, and never revisited: a
       * gesture that starts vertical belongs to the page, and one that starts
       * horizontal belongs to the rail. Committing early is what stops the rail
       * from stuttering when a finger drifts mid-scroll. Vertical gestures are
       * handed straight back (touch-action: pan-y in the stylesheet lets the
       * browser scroll them without waiting on us).
       */
      var drag = null;
      var dragged = false;

      track.addEventListener('pointerdown', function (e) {
        if (e.button) return;                 /* left button and touch only */
        dragged = false;
        drag = { id: e.pointerId, x0: e.clientX, y0: e.clientY, dx: 0, axis: null,
                 base: offsetFor(index) };
      });

      track.addEventListener('pointermove', function (e) {
        if (!drag || e.pointerId !== drag.id) return;
        var dx = e.clientX - drag.x0;
        var dy = e.clientY - drag.y0;

        if (drag.axis === null) {
          if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;   /* still a tap */
          if (Math.abs(dy) > Math.abs(dx)) { drag = null; return; }
          drag.axis = 'x';
          /* Capture, so a finger that leaves the track mid-drag keeps feeding it. */
          if (track.setPointerCapture) track.setPointerCapture(e.pointerId);
          track.style.transition = 'none';
          root.classList.add('is-dragging');
        }

        drag.dx = dx;
        track.style.transform = 'translate3d(' + (dx - drag.base) + 'px,0,0)';
      });

      function endDrag(e) {
        if (!drag || (e && e.pointerId !== drag.id)) return;
        var moved = drag.axis === 'x' ? drag.dx : 0;
        drag = null;
        root.classList.remove('is-dragging');
        track.style.transition = '';
        if (!moved) return;

        /* Whole cards travelled, so a long drag moves as far as it looks like it
           should. A flick shorter than half a card still counts as one, or a quick
           gesture would spring back and read as the rail refusing to move. */
        var s = step() || 1;
        var moves = Math.round(-moved / s);
        if (!moves) moves = Math.abs(moved) > Math.min(48, s * 0.25) ? (moved < 0 ? 1 : -1) : 0;
        index += moves;
        dragged = true;
        apply();
      }
      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);

      /* A drag that ends over a card would otherwise open it. Capture phase, so
         this runs before the card's own handler rather than after it. */
      track.addEventListener('click', function (e) {
        if (!dragged) return;
        dragged = false;
        e.preventDefault();
        e.stopPropagation();
      }, true);

      /* ------------------------------------------- click a card to centre it --
       * On a centred rail the middle card is the subject and the ones either side
       * are context, so clicking one of those should bring it to the middle rather
       * than open it -- opening a product you can only half see is not what the
       * click meant.
       *
       * Bound after setupWrap(), so `slides` is the final list and the clones are
       * in it: clicking a clone travels to the clone, and the existing
       * transitionend handler quietly swaps it for the real card afterwards.
       *
       * Capture phase, to beat the card's own link. The drag suppressor is bound
       * on the track, which is an ancestor, so it still gets first refusal and a
       * click that ends a drag never reaches this at all.
       *
       * detail === 0 is a keyboard-generated click. Those are left alone: a person
       * tabbing to a card and pressing Enter means to open it, and they have the
       * arrow buttons for travelling.
       */
      /* data-cf-click-opens: every card opens on the first click instead
         (the Olympus II product page's variations). */
      if (centred && !root.hasAttribute('data-cf-click-opens')) {
        slides.forEach(function (sl, i) {
          sl.addEventListener('click', function (e) {
            if (i === index || e.detail === 0) return;
            e.preventDefault();
            e.stopPropagation();
            index = i;
            apply();
          }, true);
        });
      }

      var t;
      window.addEventListener('resize', function () {
        /* the section may have been removed in the editor since we bound */
        if (!root.isConnected) return;
        clearTimeout(t);
        t = setTimeout(apply, 120);
      });

      apply();
      /* Images change the measured step once they land, so re-measure. */
      root.querySelectorAll('img').forEach(function (img) {
        if (!img.complete) img.addEventListener('load', apply, { once: true });
      });
    });
  }

  /* ------------------------------------------------------- spec tabs ---- */
  function initTabs(scope) {
    scope.querySelectorAll('[data-tabs]:not([data-cf-init])').forEach(function (root) {
      var tabs = Array.prototype.slice.call(root.querySelectorAll('[role="tab"]'));
      if (!tabs.length) return;
      root.setAttribute('data-cf-init', '1');

      /* The panels carry .rv, and every panel but the first starts [hidden],
         i.e. display:none. IntersectionObserver never reports a display:none
         element as intersecting, so those panels would never get .in and would
         stay at opacity:0 — the tab would open onto empty space. Reveal a panel
         as it is shown instead, which also gives the tab change its own
         transition. */
      function reveal(panel) {
        if (panel) panel.classList.add('in');
      }

      /* The travelling pill behind the active tab. Measured from the button so
         it fits labels of different lengths, and re-measured on resize. */
      var strip = root.querySelector('.specs__tabs--strip');
      var ind = strip ? strip.querySelector('[data-cf-ind]') : null;

      function moveIndicator(tab, animate) {
        if (!ind || !strip) return;
        if (!animate) ind.style.transition = 'none';
        ind.style.width = tab.offsetWidth + 'px';
        ind.style.transform = 'translateX(' + (tab.offsetLeft - strip.clientLeft) + 'px)';
        if (!animate) {
          void ind.offsetWidth;
          ind.style.transition = '';
        }
        ind.classList.add('specs__ind--ready');
      }

      /* Restart the panel's entrance so every tab change animates, not just
         the first. Removing the class and forcing a reflow is what lets the
         same animation run again. */
      function animatePanel(panel) {
        if (!panel) return;
        panel.classList.remove('specs__panel--anim');
        void panel.offsetWidth;
        panel.classList.add('specs__panel--anim');
      }

      var current = tabs.filter(function (t) {
        return t.getAttribute('aria-selected') === 'true';
      })[0] || tabs[0];

      tabs.forEach(function (t) {
        var p = root.querySelector('#' + t.getAttribute('aria-controls'));
        if (p && !p.hidden) reveal(p);
      });

      if (ind) {
        /* Fonts can land after first paint and change button widths. */
        moveIndicator(current, false);
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(function () { moveIndicator(current, false); });
        }
        var rt;
        window.addEventListener('resize', function () {
          if (!root.isConnected) return;
          clearTimeout(rt);
          rt = setTimeout(function () { moveIndicator(current, false); }, 120);
        });
        strip.addEventListener('scroll', function () { moveIndicator(current, false); });
      }

      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          tabs.forEach(function (t) {
            var on = t === tab;
            t.setAttribute('aria-selected', on ? 'true' : 'false');
            /* scope the lookup to this section: two spec sections on one page
               would otherwise fight over duplicate panel ids */
            var panel = root.querySelector('#' + t.getAttribute('aria-controls'));
            if (!panel) return;
            panel.hidden = !on;
            if (on) {
              reveal(panel);
              animatePanel(panel);
            }
          });
          current = tab;
          moveIndicator(tab, true);
        });
        tab.addEventListener('keydown', function (e) {
          var i = tabs.indexOf(tab);
          if (e.key === 'ArrowRight') tabs[(i + 1) % tabs.length].focus();
          if (e.key === 'ArrowLeft') tabs[(i - 1 + tabs.length) % tabs.length].focus();
        });
      });
    });
  }

  /* --------------------------------------------------------- accordion -- */
  function initAccordion(scope) {
    scope.querySelectorAll('[data-accordion] .faq__item:not([data-cf-init])').forEach(function (item) {
      var q = item.querySelector('.faq__q');
      if (!q) return;
      item.setAttribute('data-cf-init', '1');
      q.addEventListener('click', function () {
        var open = item.classList.toggle('open');
        q.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  /* ------------------------------------------------ continuous loops --- */
  /* Both the brand marquee and the auto-scrolling gallery translate by one
     "run" of their content. Two things have to be measured rather than assumed:

       - the run width, which is the distance from the first item to the first
         item of the next copy, so any flex gap on the track is included;
       - how many copies are needed, because a run narrower than the viewport
         leaves visible empty space at the end of the travel however the
         keyframe is written.

     Percentage-based translation (-50%) got both wrong, which is what opened
     the gap after a cycle or two. */
  function buildLoop(container, track) {
    var originals = track.__cfOriginals;
    if (!originals) {
      originals = Array.prototype.slice.call(track.children);
      track.__cfOriginals = originals;
    }
    if (!originals.length) return;

    function copies() {
      return Math.round(track.children.length / originals.length);
    }

    function addCopy() {
      var frag = document.createDocumentFragment();
      originals.forEach(function (node) {
        var c = node.cloneNode(true);
        /* duplicates of content already in the DOM: keep them out of the
           accessibility tree and out of the tab order */
        c.setAttribute('aria-hidden', 'true');
        c.querySelectorAll('a,button,input,select,textarea').forEach(function (el) {
          el.tabIndex = -1;
        });
        c.classList.add('in');
        frag.appendChild(c);
      });
      track.appendChild(frag);
    }

    /* One copy is needed just to measure the run width. */
    if (copies() < 2) addCopy();

    /* Measured from the first item actually laid out: a hidden one (a strap
       variant hides some gallery shots) has no position to measure from. */
    var lead = 0;
    while (lead < originals.length && !originals[lead].getClientRects().length) lead++;
    if (lead === originals.length) return;
    var first = track.children[lead];
    var nextRun = track.children[originals.length + lead];
    if (!first || !nextRun) return;
    var run = nextRun.offsetLeft - first.offsetLeft;
    if (run <= 0) return;

    /* Cover the viewport at the far end of the travel, plus one spare run. */
    var want = Math.max(2, Math.ceil(container.clientWidth / run) + 1);
    var guard = 0;
    while (copies() < want && guard++ < 16) addCopy();

    track.style.setProperty('--cf-run', run + 'px');
    originals.forEach(function (n) { n.classList.add('in'); });
  }

  /* Undo a loop: drop the clones and the measured run, leaving the original
     children. Needed because some rails only loop at certain widths. */
  function resetLoop(track) {
    var originals = track.__cfOriginals;
    if (!originals) return;
    while (track.children.length > originals.length) {
      track.removeChild(track.lastElementChild);
    }
    track.style.removeProperty('--cf-run');
  }

  function initLoops(scope) {
    var loops = [];

    scope.querySelectorAll('.press:not([data-cf-loop])').forEach(function (root) {
      var track = root.querySelector('.press__track');
      if (track) { root.setAttribute('data-cf-loop', '1'); loops.push([root, track, null]); }
    });
    scope.querySelectorAll('.pgallery--auto:not([data-cf-loop])').forEach(function (root) {
      var track = root.querySelector('[data-track]');
      if (track) { root.setAttribute('data-cf-loop', '1'); loops.push([root, track, null]); }
    });
    /* The variations grid becomes a rail on small screens only, so it carries
       the width at which it should loop and is torn down above it. */
    scope.querySelectorAll('[data-cf-loop-mq]:not([data-cf-loop])').forEach(function (root) {
      var track = root.querySelector('[data-cf-loop-track]');
      if (!track) return;
      root.setAttribute('data-cf-loop', '1');
      loops.push([root, track, root.getAttribute('data-cf-loop-mq')]);
    });
    if (!loops.length) return;

    function build() {
      loops.forEach(function (entry) {
        var root = entry[0], track = entry[1], mq = entry[2];
        if (!root.isConnected) return;
        if (mq && !window.matchMedia(mq).matches) {
          resetLoop(track);
          root.classList.remove('is-looping');
          return;
        }
        buildLoop(root, track);
        root.classList.add('is-looping');
      });
    }

    /* Rebuild when a conditional rail crosses its breakpoint. */
    loops.forEach(function (entry) {
      if (!entry[2] || !window.matchMedia) return;
      var m = window.matchMedia(entry[2]);
      if (m.addEventListener) m.addEventListener('change', build);
      else if (m.addListener) m.addListener(build);
    });

    build();

    /* Images settle after first paint and change the run width. */
    loops.forEach(function (pair) {
      pair[1].querySelectorAll('img').forEach(function (img) {
        if (!img.complete) img.addEventListener('load', build, { once: true });
      });
    });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(build);

    var t;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(build, 150);
    });
    /* Items shown or hidden in place (strap variants hide some gallery shots). */
    document.addEventListener('cf:reflow', build);
  }

  /* ------------------------------------------------------------ video --- */
  /* A still image that becomes a player on click. The host carries
     [data-cf-video]; a Shopify-hosted <video.cf-video__player> is revealed in
     place, an external YouTube/Vimeo embed is created on first click. */
  function play(host) {
    if (host.classList.contains('cf-playing')) return;
    var player = host.querySelector('.cf-video__player');
    if (player) {
      host.classList.add('cf-playing');
      if (typeof player.play === 'function') { player.play().catch(function () {}); }
      return;
    }
    var embed = host.getAttribute('data-cf-embed');
    if (!embed) return;
    var frame = document.createElement('iframe');
    frame.className = 'cf-video__player';
    frame.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
    frame.setAttribute('allowfullscreen', '');
    frame.setAttribute('title', host.getAttribute('data-cf-title') || 'Video');
    frame.src = embed;
    host.appendChild(frame);
    host.classList.add('cf-playing');
  }

  /* ------------------------------------------------- video, cursor-driven --
   * A film with no badge and no control bar: while the pointer is over it the
   * cursor itself shows play or pause, and clicking anywhere toggles.
   *
   * Pointer only, and the section still renders a visually-hidden button for
   * everyone else -- a cursor cannot be tabbed to or announced, so it can never
   * be the only way to work a control.
   */
  function initVideoCursor(scope) {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    scope.querySelectorAll('[data-cf-cursor-video]:not([data-cf-vcur])').forEach(function (host) {
      host.setAttribute('data-cf-vcur', '1');
      /* Added from script, never in the markup: if any of this fails the visitor
         must not be left with no pointer over the film. */
      host.classList.add('cf-cur-host');

      var el = cursorEl();
      var raf = null, mx = 0, my = 0;
      var video = host.querySelector('.cf-video__player');

      function place() { raf = null; el.style.transform = 'translate3d(' + mx + 'px,' + my + 'px,0)'; }
      function sync() { el.classList.toggle('cf-cur--playing', !!(video && !video.paused)); }
      function hide() { el.classList.remove('cf-cur--on', 'cf-cur--media', 'cf-cur--playing'); }

      host.addEventListener('pointermove', function (e) {
        if (e.pointerType === 'touch') return;
        mx = e.clientX; my = e.clientY;
        el.classList.add('cf-cur--on', 'cf-cur--media');
        sync();
        if (!raf) raf = requestAnimationFrame(place);
      });

      /* The same set of exits the rail cursor needs, and for the same reason:
         pointerleave only fires when the POINTER moves, so scrolling the film
         out from under a still cursor would strand the glyph on screen. */
      host.addEventListener('pointerleave', hide);
      host.addEventListener('pointercancel', hide);
      window.addEventListener('blur', hide);
      window.addEventListener('scroll', function () {
        if (!el.classList.contains('cf-cur--media')) return;
        var under = document.elementFromPoint(mx, my);
        if (!under || !host.contains(under)) hide();
      }, { passive: true });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          if (!entries[0].isIntersecting) hide();
        }).observe(host);
      }

      host.addEventListener('click', function (e) {
        if (e.target.closest('a,button')) return;
        if (!video) return;
        /* This slide sits inside a click-to-navigate rail, and without this the
           same click both starts the film and steps the carousel -- so the film
           you just started slides out from under the pointer. */
        e.stopPropagation();
        /* First click reveals the player and starts it with its sound on, which
           is what play() does. After that the same click is a pause toggle. */
        if (!host.classList.contains('cf-playing')) play(host);
        else if (video.paused) video.play().catch(function () {});
        else video.pause();
      });

      /* Keep the glyph honest however the state changed -- the hidden button,
         the film ending, or the browser refusing to start it. */
      if (video) {
        ['play', 'pause', 'ended'].forEach(function (ev) { video.addEventListener(ev, sync); });
      }
    });
  }
  /* ---------------------------------------------------------- film sound ----
   * A film that carries sound while it is on screen, and is silent the rest of
   * the time. Opt in per section with data-cf-sound.
   *
   * Three things make this harder than it sounds, and all three are the
   * browser's rules rather than ours:
   *
   *   1. The <video> MUST still ship muted. Autoplay is only permitted for a
   *      muted film; a video marked autoplay with sound simply never starts, and
   *      there is no sound to turn on afterwards. So muted is the starting
   *      state and this turns it off later.
   *
   *   2. Unmuting is a privileged act. Chrome and Safari refuse it — and pause
   *      the film for good measure — until the visitor has interacted with the
   *      page, and they are strict about which events count as interacting: on
   *      a touch screen it is the END of a tap, not the start. So nothing is
   *      unmuted before a qualifying gesture, the list of them is written out
   *      at the listener below, and a refusal leaves the film playing muted
   *      rather than stopped.
   *
   *   3. A film nobody can silence is a bug, whatever it says in the brief.
   *      WCAG asks for a way to stop audio that starts on its own, and the
   *      hero's existing play/pause button is that way: pausing stops the sound
   *      with the picture. Nothing new to add, but the reason the button must
   *      stay is worth writing down.
   *
   * A visitor who never clicks anything gets exactly what they get today: a
   * silent film. That is the correct outcome, not a degraded one.
   */
  var soundGestured = false;
  var soundHosts = [];

  function applyFilmSound(host) {
    var v = host.querySelector('.cf-video__player');
    if (!v) return;
    /* onScreen is written by the observer below; before it has run, treat the
       film as off screen, which is the quiet answer. */
    var want = soundGestured && host.getAttribute('data-cf-onscreen') === '1';
    if (v.muted === !want) return;   /* already right */

    var wasPlaying = !v.paused;
    v.muted = !want;
    if (!want) return;               /* muting never costs the picture */

    /* Unmuting can cost the play state, and on a phone it usually does.
     *
     * A film that autoplayed only because it was muted holds no permission to
     * make noise. Turning the sound on asks for that permission again, and when
     * the browser refuses it does not merely stay quiet -- it PAUSES the film.
     * WebKit is the one that matters here: it re-evaluates on a task of its
     * own, so at the moment the line above returns v.paused is still false and
     * a check made right here sees nothing wrong. A fraction of a second later
     * the hero is a frozen frame over the still. That is the mobile fault this
     * handles, and it is invisible on a desktop browser, which either allows
     * the unmute or pauses synchronously.
     *
     * So recovery runs twice: now, for the browsers that pause synchronously,
     * and again off the next `pause` event, for the ones that do not. And when
     * playing unmuted is refused outright, the film goes back to muted and
     * carries on -- a silent hero is the design; a stopped one is a bug.
     */
    var recover = function () {
      /* Paused on purpose, or no longer the film in view: leave it alone. Both
         also arrive as a `pause` event, and neither wants restarting. */
      if (host.classList.contains('cf-paused')) return;
      if (host.getAttribute('data-cf-onscreen') !== '1') return;
      if (!v.paused) return;
      v.play().catch(function () {
        v.muted = true;
        v.play().catch(function () {});
      });
    };

    recover();

    /* Only worth watching if there was a running film to lose. `once` plus the
       timeout keeps this to one short window rather than a listener that
       outlives the unmute and then fights the off-screen pause. */
    if (!wasPlaying) return;
    v.addEventListener('pause', recover, { once: true });
    setTimeout(function () { v.removeEventListener('pause', recover); }, 1200);
  }

  function initFilmSound(scope) {
    scope.querySelectorAll('[data-cf-sound]:not([data-cf-sound-init])').forEach(function (host) {
      host.setAttribute('data-cf-sound-init', '1');
      var v = host.querySelector('.cf-video__player');
      if (!v) return;
      /* Reduced motion already leaves the film paused; a paused film with the
         sound turned on is worse than one without. */
      if (reduced) return;
      soundHosts.push(host);

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            host.setAttribute('data-cf-onscreen', e.isIntersecting ? '1' : '0');
            applyFilmSound(host);
          });
          /* Half of it showing, so the sound arrives with the section rather
             than from the sliver of it at the bottom of the screen. */
        }, { threshold: 0.5 }).observe(host);
      } else {
        host.setAttribute('data-cf-onscreen', '1');
      }
    });

    if (soundHosts.length && !soundGestured) {
      /* The events that actually grant permission, which is not the obvious
       * set, and is the whole of why this worked on a desktop and not on a
       * phone.
       *
       * The HTML spec calls these "activation triggering input events". On a
       * touch screen neither `touchstart` nor `pointerdown` is one: pointerdown
       * only counts when its pointerType is "mouse", and the touch equivalents
       * are `pointerup` and `touchend`. The old list was pointerdown, keydown,
       * touchstart -- so on a desktop the first click arrived as a qualifying
       * pointerdown and the sound came on, while on a phone the handler ran at
       * touchstart, BEFORE the tap had granted anything. The unmute was refused
       * there, and since the listeners took themselves off in the same breath
       * there was never a second attempt.
       *
       * pointerdown stays for the mouse; pointerup, touchend and click are what
       * a finger actually produces.
       */
      var gestures = ['pointerdown', 'pointerup', 'touchend', 'keydown', 'click'];

      var wake = function () {
        soundGestured = true;
        soundHosts.forEach(applyFilmSound);

        /* Let go only once the sound has actually landed somewhere. A tap made
           while every film is off screen unmutes nothing, and dropping the
           listeners there would spend the one chance on nothing at all -- the
           visitor would scroll down to a hero that stays silent for good.
           Staying subscribed costs a no-op call per tap and means the next
           gesture after the film is in view still works. */
        var live = soundHosts.some(function (h) {
          var v = h.querySelector('.cf-video__player');
          return v && !v.muted;
        });
        if (!live) return;

        gestures.forEach(function (t) {
          document.removeEventListener(t, wake, true);
        });
      };

      gestures.forEach(function (t) {
        document.addEventListener(t, wake, true);
      });
    }

    /* A tab in the background should not be talking. */
    if (soundHosts.length && !document.__cfSoundVis) {
      document.__cfSoundVis = true;
      document.addEventListener('visibilitychange', function () {
        soundHosts.forEach(function (h) {
          var v = h.querySelector('.cf-video__player');
          if (!v) return;
          if (document.hidden) v.muted = true;
          else applyFilmSound(h);
        });
      });
    }
  }

  function initVideo(scope) {
    /* Films that start on their own. The <video> already carries autoplay and
       muted, so the browser handles the starting; what this does is reveal the
       player (the still sits above it until .cf-playing lands) and record the
       outcome, because autoplay is a request, not a guarantee -- a browser may
       refuse it, and then the button has to read Play rather than Pause.

       Not under reduced motion. A looping film is exactly the kind of movement
       that preference is about, so there it waits to be asked. */
    scope.querySelectorAll('[data-cf-auto]:not([data-cf-auto-init])').forEach(function (host) {
      host.setAttribute('data-cf-auto-init', '1');
      /* Not inside an active-slide rail. Those films are built in the autoplay
         shape -- muted, playsinline, revealed by the [data-cf-auto] rules in the
         stylesheet -- but WHICH of them runs is initActiveFilm's decision, and
         starting them all here is the whole thing it exists to prevent. Marked
         initialised above first, so this is a hand-off and not a retry. */
      if (host.closest('[data-cf-active-film]')) return;
      var v = host.querySelector('.cf-video__player');
      if (!v) return;
      if (reduced) { host.classList.add('cf-playing', 'cf-paused'); return; }

      /* Started when it is nearly in view, and stopped again when it is well
         out of it. The markup no longer carries autoplay or any preload, so
         until this runs the film has cost nothing but a line of HTML.

         .cf-playing goes on at the first successful play rather than up front:
         it is what reveals the player over the still, and revealing a film that
         has not begun decoding shows a black rectangle where the photograph
         was. Once on it stays on — taking it off when the film scrolls away
         would flash the still back as it left. */
      var play = function () {
        if (host.classList.contains('cf-paused')) return;   /* paused by hand */
        var started = v.play();
        if (started && started.then) {
          started.then(function () { host.classList.add('cf-playing'); },
                       function () { host.classList.add('cf-playing', 'cf-paused'); });
        } else {
          host.classList.add('cf-playing');
        }
      };

      if (!('IntersectionObserver' in window)) { play(); return; }

      /* Two ranges, and the outer one is the fix for "sometimes it plays and
         sometimes it is very late".
       *
       * There used to be one observer at 300px doing both jobs. The markup
       * carries preload="none", so nothing was fetched until that margin was
       * crossed -- and then play() had to open the connection, buffer, and
       * decode a first frame before its promise resolved and the film was
       * revealed. 300px is a fraction of a second of scrolling. On a small file
       * that is invisible; on a large one the still sits there until the buffer
       * catches up, which is exactly the inconsistency being reported. Whether
       * it looked instant depended on file size and connection, so it varied
       * run to run on the same page.
       *
       * Split, each range does one job:
       *
       *   1500px  start BUFFERING. preload=auto and load() open the connection
       *           early, while the film is still well out of sight, so the
       *           bytes are arriving during the scroll rather than after it.
       *           Once only -- the flag is what stops a scroll that crosses the
       *           boundary repeatedly from calling load() again and throwing
       *           away the buffer it just built.
       *
       *    300px  start PLAYING, as before. By now there is something buffered
       *           to play, so the promise resolves quickly and .cf-playing
       *           reveals a film with pixels in it rather than a black frame.
       *
       * This shortens the wait; it cannot remove it. A film that is hundreds of
       * megabytes will still be late from a cold start, because the bytes have
       * to arrive either way -- see the note in cf-video-player.liquid about
       * what the source files actually cost. */
      var warmed = false;
      var warm = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting || warmed) return;
          warmed = true;
          v.preload = 'auto';
          if (typeof v.load === 'function') v.load();
          /* Its whole job is done at the first crossing. */
          warm.disconnect();
        });
      }, { rootMargin: '1500px 0px' });
      warm.observe(host);

      /* Threshold 0 so a film taller than the screen still counts as seen.
         Pausing on the way out is half the saving: a film left running off
         screen keeps pulling the rest of itself down. */
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) play();
          else if (!v.paused) v.pause();
        });
      }, { rootMargin: '300px 0px' }).observe(host);
    });

    scope.querySelectorAll('[data-cf-play]:not([data-cf-init])').forEach(function (btn) {
      btn.setAttribute('data-cf-init', '1');

      var playLabel = btn.getAttribute('data-cf-label-play') || 'Play film';
      var pauseLabel = btn.getAttribute('data-cf-label-pause') || 'Pause film';

      btn.addEventListener('click', function () {
        var host = btn.closest('[data-cf-video]');
        if (!host) return;

        /* An autoplaying film's button is a pause toggle. A still's button is a
           one-way trigger that reveals the player with its sound on. */
        if (host.hasAttribute('data-cf-auto')) {
          var v = host.querySelector('.cf-video__player');
          if (!v) return;
          if (v.paused) {
            v.play().catch(function () {});
            host.classList.remove('cf-paused');
            btn.setAttribute('aria-label', pauseLabel);
          } else {
            v.pause();
            host.classList.add('cf-paused');
            btn.setAttribute('aria-label', playLabel);
          }
          return;
        }

        play(host);
      });
    });
  }

  /* ----------------------------------------------------- lightbox ------ */
  /* One full-screen overlay, shared by everything on the page that opens.
   *
   * Built once and reused rather than one per card: the alternative is a hidden
   * copy of every image and every film in the document, which on the collection
   * page is a dozen videos the browser may decide to preload.
   *
   * What goes into it is a NODE, not a URL -- the caller hands over a clone of the
   * thing that was clicked. For an image that keeps the srcset, so the full-screen
   * copy is picked for the full-screen size rather than upscaled from the card.
   * For a film it keeps the element's current time, so it carries on where the card
   * left off instead of restarting.
   */
  var lb = null;
  function lightbox() {
    if (lb) return lb;
    var root = document.createElement('div');
    root.className = 'cf-lb';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.hidden = true;
    root.innerHTML =
      '<div class="cf-lb__stage" data-stage></div>' +
      '<button class="cf-lb__close" type="button" data-close aria-label="Close">' +
        '<svg viewBox="0 0 24 24" aria-hidden="true">' +
          '<path d="M5 5l14 14M19 5L5 19" fill="none" stroke="currentColor" stroke-width="1.5"/>' +
        '</svg>' +
      '</button>';
    document.body.appendChild(root);

    var stage = root.querySelector('[data-stage]');
    var closeBtn = root.querySelector('[data-close]');
    var opener = null;

    function close() {
      if (root.hidden) return;
      root.hidden = true;
      root.classList.remove('is-open');
      document.documentElement.classList.remove('cf-lb-open');
      /* Emptied on close, not left loaded. A paused <video> still holds its buffer
         and a decoded full-screen image still holds its bitmap. */
      stage.innerHTML = '';
      if (opener && opener.isConnected) opener.focus({ preventScroll: true });
      opener = null;
      announce(false);
    }

    /* The overlay says when it is up, rather than everything that cares polling
       for the class on <html>. What cares is anything that makes a noise: the
       full-screen copy of a film has its sound on, and the rail it was opened
       from must not keep talking underneath it. */
    function announce(open) {
      document.dispatchEvent(new CustomEvent('cf:lightbox', { detail: { open: open } }));
    }

    closeBtn.addEventListener('click', close);
    /* The backdrop closes, the content does not -- so a click that lands on the
       photograph itself is not read as a request to dismiss it. */
    root.addEventListener('click', function (e) {
      if (e.target === root || e.target === stage) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });

    lb = {
      open: function (node, from) {
        stage.innerHTML = '';
        stage.appendChild(node);
        opener = from || null;
        root.hidden = false;
        /* A frame between unhiding and the class, or the opening transition has
           nothing to transition from. */
        requestAnimationFrame(function () { root.classList.add('is-open'); });
        document.documentElement.classList.add('cf-lb-open');
        closeBtn.focus({ preventScroll: true });
        announce(true);
      },
      close: close
    };
    return lb;
  }

  /* A copy of a card's artwork, sized for the screen rather than for the card. */
  function fullImage(img) {
    var big = img.cloneNode(true);
    big.className = 'cf-lb__img';
    big.removeAttribute('width');
    big.removeAttribute('height');
    big.loading = 'eager';
    /* The card asked for a quarter of the viewport; this one fills it. Without
       rewriting sizes the browser keeps the small candidate it already chose. */
    big.sizes = '100vw';
    return big;
  }

  /* ------------------------------------------------ cards that open full --
   * Any [data-cf-zoom] opens its own image full screen.
   *
   * On the collection cards the hook sits on .pcard__media -- the photograph --
   * and not on the card around it. Three behaviours, one per region, and no
   * overlap between them:
   *
   *   the photograph  opens full screen
   *   Discover        goes to the product page
   *   anywhere else   does nothing at all
   *
   * It used to be bound to the whole card, from when the shop was
   * invitation-only and a card had nowhere to link to, so the click had to do
   * something. With the cards linking now, a card-wide hook meant the name, the
   * meta line and the dead space between them all opened a lightbox -- three
   * targets that look like nothing, next to a link that is one.
   */
  function initZoom(scope) {
    scope.querySelectorAll('[data-cf-zoom]:not([data-cf-zoom-init])').forEach(function (el) {
      el.setAttribute('data-cf-zoom-init', '1');
      var img = el.querySelector('img');
      if (!img) return;

      /* A marquee clone is a duplicate of content already in the document: cf.js
         hides it from the accessibility tree and drops it out of the tab order on
         purpose. It still opens on click -- it is what is under the pointer -- but
         it must not be announced or tabbed to a second time.

         closest() rather than a read of this element's own attribute: the hook
         moved from the card onto the photograph inside it, and aria-hidden is set
         on the clone's root. Asked of the photograph directly the answer is
         always null, and every clone would have been given a role and a tab stop
         of its own. */
      if (!el.closest('[aria-hidden="true"]')) {
        if (!el.hasAttribute('tabindex')) el.tabIndex = 0;
        el.setAttribute('role', 'button');
      }

      function open(e) {
        /* Kept from when this was bound to the whole card, where the CTA sat
           inside the hook and had to be let through. Nothing links inside a
           .pcard__media today, so it guards nothing -- and it is two lines that
           stop the next caption or badge with a link in it from being swallowed. */
        if (e.target.closest('a[href],button')) return;
        e.preventDefault();
        lightbox().open(fullImage(img), el);
      }
      el.addEventListener('click', open);
      el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') open(e);
      });
    });
  }

  /* ------------------------------------------- active-slide film rail --- */
  /* The collector testimonials. Only the slide in the middle plays; clicking it
   * opens it full screen; and when its film runs out the rail moves on by itself.
   *
   * Only one at a time, and that is the point rather than an optimisation: a rail
   * of simultaneously playing films is both unwatchable and, on a phone, several
   * video decoders running for footage nobody is looking at. The others are paused
   * AND rewound, so a slide returned to opens on its first frame rather than
   * halfway through.
   *
   * It starts muted, because that is the only shape a browser will start on its
   * own. Whether it STAYS muted is initSound's business below: the rail is meant
   * to speak once it is actually on screen and being looked at.
   */
  function initActiveFilm(scope) {
    scope.querySelectorAll('[data-cf-active-film]:not([data-cf-af])').forEach(function (root) {
      root.setAttribute('data-cf-af', '1');
      var next = root.querySelector('[data-next]');
      var current = null;

      /* ------------------------------------------------------- sound ----
       * The rail is silent until it is on screen, then the film that is live
       * speaks; scroll away and it goes quiet again. Nothing is audible from a
       * section nobody is looking at, and nothing has to be clicked to hear the
       * one they are.
       *
       * Three things can take the voice away, and they are tracked separately
       * because they come back separately: the section leaving the viewport,
       * the visitor muting it by hand, and the full-screen copy taking over.
       */
      var wantSound = root.hasAttribute('data-cf-sound');
      var inView = false;
      var offByHand = false;
      var overlaid = false;
      var armed = false;          /* waiting on a first gesture to be allowed */
      var byHand = false;         /* the visitor asked for sound with the control */

      function audible() {
        return wantSound && inView && !offByHand && !overlaid && (!reduced || byHand);
      }

      /* Ask for sound, and cope with being told no.
       *
       * Autoplay policy is the whole difficulty here. A film may start muted
       * without permission, but the moment it becomes audible the browser wants
       * a user gesture behind it -- and it refuses in two different ways: some
       * reject the play() promise, others accept it and then quietly pause the
       * element. So the promise is not trusted on its own; the state is read
       * back once the decision has been made.
       *
       * Refused, it goes back to muted playback rather than to silence-and-stop:
       * a visible film frozen mid-frame is a worse outcome than a quiet one. The
       * request is then re-made at the visitor's first click or keypress
       * anywhere on the page, which is the point permission arrives. */
      function speak(v) {
        v.muted = false;
        var p = v.play();
        if (p && p.catch) p.catch(function () { refused(v); });
        setTimeout(function () {
          if (v === current && audible() && v.paused) refused(v);
        }, 250);
      }

      function refused(v) {
        if (v !== current) return;
        v.muted = true;
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
        arm();
        sync();
      }

      /* Which event carries the permission differs by input, and that is what
         broke this on phones. A mouse grants it at pointerdown; a finger only at
         the lift -- pointerup / touchend -- so a touch pointerdown retried too
         early, was refused again, and the lift that WOULD have worked had already
         gone by. So every candidate is listened for, and one that does not carry
         permission is let pass rather than spent.

         A press on the sound control itself is left to the control: retrying
         here as well would unmute on the press and have its own click mute it
         straight back. */
      var GESTURES = ['pointerdown', 'pointerup', 'touchend', 'click', 'keydown'];

      function arm() {
        if (armed) return;
        armed = true;
        function retry(e) {
          if (e.target && e.target.closest && e.target.closest('[data-cf-sound-toggle]')) return;
          var ua = navigator.userActivation;
          if (ua ? !ua.isActive : (e.type === 'pointerdown' && e.pointerType !== 'mouse')) return;
          armed = false;
          GESTURES.forEach(function (n) { document.removeEventListener(n, retry, true); });
          applySound();
        }
        GESTURES.forEach(function (n) { document.addEventListener(n, retry, true); });
      }

      function applySound() {
        if (!current) return;
        var on = audible();
        if (on === !current.muted) return;      /* already as it should be */
        if (on) speak(current);
        else current.muted = true;
        sync();
      }

      /* The toggles live on every slide but only the active one is on show, so
         all of them are kept in step rather than hunting for the visible one. */
      function sync() {
        var on = !!current && !current.muted;
        root.querySelectorAll('[data-cf-sound-toggle]').forEach(function (btn) {
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');
          btn.classList.toggle('is-on', on);
          var label = on ? 'Mute the film' : 'Unmute the film';
          btn.setAttribute('aria-label', label);
          btn.title = label;
        });
        root.classList.toggle('collectors--sound', on);
      }

      if (wantSound) {
        root.addEventListener('click', function (e) {
          var btn = e.target.closest('[data-cf-sound-toggle]');
          if (!btn || !root.contains(btn)) return;
          /* Ahead of the open-full-screen handler below, which is bound on this
             same root: without stopping here, muting would also open the film. */
          e.preventDefault();
          e.stopPropagation();
          /* Read from what is actually heard, not from the flag: a film the
             browser kept muted has offByHand false already, and flipping it
             would turn "unmute" into "mute". This click is itself the gesture
             the browser wanted, so asking for sound here is always allowed --
             on a phone included, and under reduced motion, where the film is
             not otherwise started. */
          if (!current) return;
          if (!current.muted) {
            offByHand = true;
            current.muted = true;
            sync();
            return;
          }
          offByHand = false;
          byHand = true;
          speak(current);
          sync();
        }, true);

        /* The full-screen copy is unmuted by design. Two of the same film
           talking over each other is the one case sound here must not produce. */
        document.addEventListener('cf:lightbox', function (e) {
          overlaid = !!(e.detail && e.detail.open);
          applySound();
        });
      }

      function filmIn(slide) {
        return slide ? slide.querySelector('video.cf-video__player') : null;
      }

      function advance() {
        if (next) next.click();
      }

      function setActive(slide, slides) {
        slides.forEach(function (sl) {
          sl.classList.toggle('cslide--active', sl === slide);
          var v = filmIn(sl);
          if (!v || sl === slide) return;
          v.pause();
          try { v.currentTime = 0; } catch (err) {}
        });

        var v = filmIn(slide);
        if (v === current) return;

        if (current) current.removeEventListener('ended', advance);
        current = v;
        if (!v) return;

        /* `loop` is cleared on whichever film is live, and that is deliberate: a
           looping video never fires `ended`, so leaving it set would silently
           disable the auto-advance. A slide that is not the active one keeps its
           loop attribute and simply sits paused. */
        v.loop = false;
        /* Muted to start whatever the rail sounds like, because this is where a
           film BEGINS playing and a browser decides that by reading the element
           as it is at that moment. applySound then asks for the voice back, by
           which point the film is already running and the ask is a change of
           state rather than a request to start an audible one. */
        v.muted = true;
        v.addEventListener('ended', advance);
        if (reduced) { sync(); return; }
        var started = v.play();
        if (started && started.catch) started.catch(function () {});
        applySound();
      }

      root.addEventListener('cf:slide', function (e) {
        setActive(e.detail.active, e.detail.slides);
      });

      /* Nothing should be playing -- or talking -- behind the visitor's back.
         The same crossing is what gives the live film its sound and what takes
         it away again, so both hang off this one observer rather than off two
         that could disagree about whether the rail is on screen. */
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          inView = entries[0].isIntersecting;
          if (!current) return;
          if (inView) {
            if (!reduced) current.play().catch(function () {});
          } else current.pause();
          applySound();
        }, { threshold: 0.25 }).observe(root);
      } else {
        inView = true;
      }

      /* Click the live slide to open it full screen, with its sound on. The rail
         already intercepts clicks on the OTHER slides to centre them, in the
         capture phase, so those never reach this. */
      root.addEventListener('click', function (e) {
        var slide = e.target.closest('.cslide');
        if (!slide || !slide.classList.contains('cslide--active')) return;
        if (e.target.closest('a[href],button')) return;
        var v = filmIn(slide);
        var node;
        if (v) {
          node = v.cloneNode(true);
          node.className = 'cf-lb__video';
          node.muted = false;
          node.controls = true;
          node.loop = true;
          node.autoplay = true;
          /* Picked up where the card had got to rather than restarted. */
          node.currentTime = v.currentTime;
        } else {
          var img = slide.querySelector('img');
          if (!img) return;
          node = fullImage(img);
        }
        e.preventDefault();
        e.stopPropagation();
        lightbox().open(node, slide);
      });
    });
  }

  /* ------------------------------------------------ expand cursor ------- */
  /* The affordance for anything that opens full screen: over a thing that will,
     the pointer becomes a full-screen glyph, so the thing itself carries no
     badge across the photograph. Pointer only; the real button or the card's own
     role=button is what serves everyone else.

     Which descendants are "a thing that will" is the attribute's value, because
     two sections want this cursor over different targets: the collector rail
     over its live slide only -- the neighbours are context, and clicking one
     centres it, which is a different gesture wanting the ordinary pointer -- and
     the product grid over every zoomable card. Bare, it means the former, which
     is what the collector rail still writes. */
  function initExpandCursor(scope) {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    scope.querySelectorAll('[data-cf-expand]:not([data-cf-exp])').forEach(function (root) {
      root.setAttribute('data-cf-exp', '1');
      var sel = root.getAttribute('data-cf-expand') || '.cslide--active';
      var el = cursorEl();
      var raf = null, mx = 0, my = 0, over = false;

      function place() { raf = null; el.style.transform = 'translate3d(' + mx + 'px,' + my + 'px,0)'; }
      function hide() {
        over = false;
        root.classList.remove('cf-cur-host');
        el.classList.remove('cf-cur--on', 'cf-cur--expand');
      }

      root.addEventListener('pointermove', function (e) {
        if (e.pointerType === 'touch') return;
        mx = e.clientX; my = e.clientY;
        /* A link or a button inside the target does something else -- follows a
           link, mutes a film -- so the glyph that promises full screen stands
           down over it and the ordinary pointer comes back. */
        if (e.target.closest('a[href],button')) { if (over) hide(); return; }
        /* closest() walks past the root as happily as up to it, so a loose
           selector could match an ancestor of the whole section. Confirm the hit
           is inside what we were pointed at. */
        var live = e.target.closest(sel);
        if (!live || !root.contains(live)) { if (over) hide(); return; }
        over = true;
        root.classList.add('cf-cur-host');
        el.classList.add('cf-cur--on', 'cf-cur--expand');
        if (!raf) raf = requestAnimationFrame(place);
      });

      /* The same exits the other cursors need: pointerleave only fires when the
         POINTER moves, so scrolling the rail out from under a still cursor would
         strand the glyph on screen. */
      root.addEventListener('pointerleave', hide);
      root.addEventListener('pointercancel', hide);
      window.addEventListener('blur', hide);
      window.addEventListener('scroll', function () {
        if (!over) return;
        var under = document.elementFromPoint(mx, my);
        if (!under || !root.contains(under)) hide();
      }, { passive: true });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          if (!entries[0].isIntersecting) hide();
        }).observe(root);
      }
    });
  }

  /* ---------------------------------------------------- pointer drift --- */
  /* Writes the pointer's offset from the section centre as -1..1, for CSS to
     turn into translate/rotate. Sampled on rAF so a fast pointer cannot queue
     more style writes than the browser paints. */
  function initDrift(scope) {
    if (reduced) return;
    scope.querySelectorAll('[data-cf-drift]:not([data-cf-init])').forEach(function (root) {
      root.setAttribute('data-cf-init', '1');
      var target = root.querySelector('[data-cf-drift-target]') || root;
      var raf = null, px = 0, py = 0;

      function write() {
        raf = null;
        target.style.setProperty('--cf-px', px.toFixed(3));
        target.style.setProperty('--cf-py', py.toFixed(3));
      }

      root.addEventListener('pointermove', function (e) {
        if (e.pointerType === 'touch') return;
        var r = root.getBoundingClientRect();
        if (!r.width || !r.height) return;
        px = ((e.clientX - r.left) / r.width - 0.5) * 2;
        py = ((e.clientY - r.top) / r.height - 0.5) * 2;
        root.classList.add('bracelet--tracking');
        if (!raf) raf = requestAnimationFrame(write);
      });

      root.addEventListener('pointerleave', function () {
        px = 0; py = 0;
        root.classList.remove('bracelet--tracking');
        if (!raf) raf = requestAnimationFrame(write);
      });
    });
  }

  /* --------------------------------------------- cursor-led navigation -- */
  /* One floating control shared by every rail on the page. Which half of the
     rail the pointer is in decides the direction, and a click advances. The
     rail's real prev/next buttons stay in the DOM and are driven from here, so
     bounds handling and keyboard access keep working. */
  var cur = null;
  function cursorEl() {
    if (cur) return cur;
    cur = document.createElement('div');
    cur.className = 'cf-cur';
    cur.setAttribute('aria-hidden', 'true');
    /* Three glyphs in one element, picked by class. The rails want a chevron,
       a film wants play or pause, and they never appear at the same moment --
       so one cursor carries all three rather than the page owning several. */
    cur.innerHTML =
      '<span class="cf-cur__in">' +
        '<svg class="cf-cur__arrow" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7"/></svg>' +
        '<svg class="cf-cur__play" viewBox="0 0 10 12"><path d="M0 0l10 6-10 6z"/></svg>' +
        '<svg class="cf-cur__pause" viewBox="0 0 10 12"><path d="M0 0h3v12H0zM7 0h3v12H7z"/></svg>' +
        '<svg class="cf-cur__expand" viewBox="0 0 24 24">' +
          '<path d="M9 3H3v6M15 3h6v6M15 21h6v-6M9 21H3v-6"/>' +
        '</svg>' +
      '</span>';
    document.body.appendChild(cur);
    return cur;
  }

  function initCursorNav(scope) {
    var fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!fine) return;

    scope.querySelectorAll('.cf-cursor-nav:not([data-cf-cur])').forEach(function (root) {
      var prev = root.querySelector('[data-prev]');
      var next = root.querySelector('[data-next]');
      if (!prev && !next) return;
      root.setAttribute('data-cf-cur', '1');

      var el = cursorEl();
      var dir = 'next';
      var raf = null, mx = 0, my = 0;

      function place() {
        raf = null;
        el.style.transform = 'translate3d(' + mx + 'px,' + my + 'px,0)';
      }

      root.addEventListener('pointermove', function (e) {
        if (e.pointerType === 'touch') return;
        mx = e.clientX; my = e.clientY;

        /* Direction comes from the pointer's side of the rail's own mid-line,
           which is what the reader is actually looking at. */
        var r = root.getBoundingClientRect();
        dir = e.clientX < r.left + r.width / 2 ? 'prev' : 'next';
        var btn = dir === 'prev' ? prev : next;

        el.classList.add('cf-cur--on');
        el.classList.toggle('cf-cur--prev', dir === 'prev');
        el.classList.toggle('cf-cur--end', !!(btn && btn.disabled));
        if (!raf) raf = requestAnimationFrame(place);
      });

      function hide() { el.classList.remove('cf-cur--on'); }

      /* pointerleave alone is not enough, and this is the bug it caused: the
       * event only fires when the POINTER moves off the rail. Scroll the page
       * with the wheel and the rail slides out from under a pointer that never
       * moved, so nothing fires and the overlay is left stranded on screen with
       * nothing under it. Same for tabbing away, and for the pointer leaving the
       * window entirely.
       *
       * So: hide on the pointer leaving, on the gesture being cancelled, on the
       * window losing focus, and -- the case that actually bit -- on any scroll
       * after which the point under the cursor is no longer inside the rail. */
      root.addEventListener('pointerleave', hide);
      root.addEventListener('pointercancel', hide);
      window.addEventListener('blur', hide);

      window.addEventListener('scroll', function () {
        if (!el.classList.contains('cf-cur--on')) return;
        var under = document.elementFromPoint(mx, my);
        if (!under || !root.contains(under)) hide();
      }, { passive: true });

      /* And if the rail itself leaves the viewport there is nothing to point at,
         whatever elementFromPoint says about the pixel. */
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          if (!entries[0].isIntersecting) hide();
        }).observe(root);
      }

      root.addEventListener('click', function (e) {
        /* let real links and the hidden buttons behave normally */
        if (e.target.closest('a,button')) return;
        /* a film inside the rail owns its own clicks; stepping the rail as well
           would carry it off screen the moment it started */
        if (e.target.closest('[data-cf-cursor-video]')) return;
        var btn = dir === 'prev' ? prev : next;
        if (btn && !btn.disabled) btn.click();
      });
    });
  }

  /* ---------------------------------------------------- looped rail nav --- */
  /* A marquee has no slides to advance, so its prev/next flip which way the loop
     runs instead. initCursorNav above already routes a click anywhere on the rail
     to whichever button matches the pointer's side, so this only has to say what
     the buttons do. */
  function initLoopNav(scope) {
    scope.querySelectorAll('[data-cf-loop-nav]:not([data-cf-loop-nav-on])').forEach(function (root) {
      root.setAttribute('data-cf-loop-nav-on', '1');

      /* Two kinds of rail behind the same pointer control.
         A hand-driven rail is a real scroller, so prev/next move it by one card.
         A self-scrolling one has no scroll position to move -- it is a keyframe --
         so there the buttons flip which way it runs. */
      var scroller = root.hasAttribute('data-cf-scroll-nav')
        ? root.querySelector('[data-cf-loop-track]')
        : null;

      root.querySelectorAll('[data-prev],[data-next]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var back = btn.hasAttribute('data-prev');
          if (!scroller) {
            root.classList.toggle('is-reversed', back);
            return;
          }
          var card = scroller.firstElementChild;
          /* One card plus the gap, measured off the layout rather than assumed,
             so the step stays right at every breakpoint. */
          var step = card ? card.getBoundingClientRect().width : scroller.clientWidth / 3;
          if (card && card.nextElementSibling) {
            step = card.nextElementSibling.getBoundingClientRect().left
                 - card.getBoundingClientRect().left;
          }
          scroller.scrollBy({ left: back ? -step : step, behavior: 'smooth' });
        });
      });
    });
  }

  /* --------------------------------------------------------------- buy --- */
  /* Variant radios post a valid id on their own, so this only refreshes the
     displayed price and button state after a choice. */
  function initBuy(scope) {
    scope.querySelectorAll('[data-cf-buy]:not([data-cf-init])').forEach(function (root) {
      var radios = root.querySelectorAll('input[name="id"][type="radio"]');
      if (!radios.length) return;
      root.setAttribute('data-cf-init', '1');

      var priceEl = root.querySelector('[data-cf-price]');
      var compareEl = root.querySelector('[data-cf-compare]');
      var badgeEl = root.querySelector('[data-cf-badge]');
      var atc = root.querySelector('[data-cf-atc]');
      var soldOut = atc ? atc.getAttribute('data-sold-out-label') : null;
      var inStock = atc ? atc.getAttribute('data-add-label') : null;

      var variantInput = root.querySelector('[data-cf-variant-input]');

      /* The price is swapped for a new element rather than having its text
         rewritten. The currency converter app keeps a record of every price it
         has converted and only re-reads one on its next pass; editing the text
         of a converted price left it showing the previous variant's figure (or
         the unconverted one) until then, and the old 60/250ms "hold the line"
         check below read the converted text as wrong and put the shop-currency
         price back -- so the price visibly lagged a click. A fresh element is
         simply a new price to the converter, with nothing stale to compare. */
      function freshPrice(el, text, variantId) {
        if (!el || !el.parentNode) return el;
        if (el.getAttribute('data-cf-variant') === String(variantId)) return el;
        var n = document.createElement(el.tagName);
        Array.prototype.forEach.call(el.attributes, function (a) {
          // The converter's marker and the display:none it hides a converted
          // original with both go: it skips hidden elements, so a copy of that
          // style would never be converted and the price would go blank.
          if (a.name.indexOf('data-cbb') === 0 || a.name === 'style') return;
          n.setAttribute(a.name, a.value);
        });
        n.className = Array.prototype.filter.call(el.classList, function (c) {
          return c.indexOf('cbb') !== 0;
        }).join(' ');
        n.setAttribute('data-cf-variant', variantId);
        n.textContent = text;
        el.parentNode.replaceChild(n, el);
        nudgeConverter();
        return n;
      }

      /* Ask the converter to convert now. Left to itself it re-scans on DOM
         changes but drops any change within 150ms of its last pass, with no
         trailing call -- so two quick clicks left the second variant showing
         the first one's converted price until something unrelated changed on
         the page, which could be never. Its API's convertPrices() runs a pass
         on the spot; the text ticks after it are the fallback for a converter
         build without that API, each one a DOM change that earns a pass. */
      var tick = null;
      var nudging = false;
      function nudgeConverter() {
        if (!window.codeblackbelt || nudging) return;
        nudging = true;
        setTimeout(function () { nudging = false; }, 0);
        var api = window.codeblackbelt['currency-converter-plusApi'];
        if (api && typeof api.convertPrices === 'function') {
          try { api.convertPrices(); } catch (e) {}
        }
        if (!tick) {
          tick = document.createElement('span');
          tick.hidden = true;
          tick.setAttribute('aria-hidden', 'true');
          root.appendChild(tick);
        }
        [170, 340, 700].forEach(function (ms) {
          setTimeout(function () { tick.textContent = tick.textContent ? '' : '\u200b'; }, ms);
        });
      }

      function sync(radio) {
        var onSale = radio.dataset.onSale === 'true';
        var available = radio.dataset.available === 'true';
        if (priceEl) priceEl = freshPrice(priceEl, radio.dataset.price || priceEl.textContent, radio.value);
        if (compareEl) {
          compareEl = freshPrice(compareEl, radio.dataset.compare || '', radio.value);
          compareEl.hidden = !onSale;
        }
        // The value setter is what xflow hooks, so this alone moves its
        // Notify me button onto the chosen variant.
        if (variantInput && variantInput.value !== radio.value) variantInput.value = radio.value;
        if (badgeEl) badgeEl.hidden = !onSale;
        if (atc) {
          atc.disabled = !available;
          if (inStock && soldOut) atc.textContent = available ? inStock : soldOut;
        }
      }

      var strap = strapSwapper(root);

      // A leather variant has no bracelet, so the bracelet section swaps to its
      // strap text while one is picked (see .cf-leather in cf.css).
      function leather(radio) {
        var on = (radio.dataset.title || '').toLowerCase().indexOf('leather') !== -1;
        document.documentElement.classList.toggle('cf-leather', on);
      }

      /* Per-option picker (Bezel / Strap / Warranty). The buttons choose values;
         the variant is whichever hidden radio carries exactly those values, and
         clicking it runs everything the radios already drive -- price, strap
         shots, the warranty note -- so none of that is duplicated here. */
      var cfg = root.querySelector('[data-cf-options]');
      var groups = cfg ? cfg.querySelectorAll('[data-cf-option]') : [];
      var summary = cfg ? cfg.querySelector('[data-cf-summary]') : null;

      function optionsOf(radio) {
        return (radio.getAttribute('data-options') || '').split('|');
      }

      function showOptions(radio) {
        if (!cfg) return;
        var vals = optionsOf(radio);
        var longs = [];
        groups.forEach(function (g) {
          var i = +g.getAttribute('data-cf-option');
          var cur = g.querySelector('[data-cf-option-current]');
          g.querySelectorAll('.pbuy__val').forEach(function (b) {
            var on = b.getAttribute('data-value') === vals[i];
            b.classList.toggle('is-on', on);
            b.setAttribute('aria-pressed', on ? 'true' : 'false');
            if (on) {
              if (cur) cur.textContent = b.getAttribute('data-short');
              longs[i] = b.getAttribute('data-long');
            }
          });
        });
        if (summary) summary.textContent = longs.filter(Boolean).join(' · ');
      }

      /* The buttons own the selection. The variant wanted is read off the
         buttons that are lit, not off the hidden radios, and the button click
         itself checks the radio and writes the price -- it no longer waits on
         the radio's click/change round trip. On the live store something else
         on the page (an app listening to the product form) could move the
         checked radio or rewrite the price after that round trip, which left
         the price one click behind: each click showed the previous variant. */
      var chosen = null;

      function selectedValues() {
        var checked = root.querySelector('input[name="id"][type="radio"]:checked') || radios[0];
        var vals = optionsOf(checked);
        groups.forEach(function (g) {
          var on = g.querySelector('.pbuy__val.is-on');
          if (on) vals[+g.getAttribute('data-cf-option')] = on.getAttribute('data-value');
        });
        return vals;
      }

      /* ?variant= in the address, as Shopify themes keep it: a shared or
         reloaded link opens on the same watch, and the converter and xflow
         both read the chosen variant from here as a fallback. */
      function rememberVariant(radio) {
        try {
          var url = new URL(window.location.href);
          if (url.searchParams.get('variant') === radio.value) return;
          url.searchParams.set('variant', radio.value);
          history.replaceState(history.state, '', url.toString());
        } catch (e) {}
      }

      function choose(radio) {
        chosen = radio;
        radios.forEach(function (r) { r.checked = r === radio; });
        apply(radio);
        rememberVariant(radio);
        // Let anything else listening to the form know. variant:change is the
        // event the currency converter and the sticky notify button listen for.
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('variant:change', {
          detail: { variantId: Number(radio.value) }
        }));
        /* Hold the line against something moving the checked radio after us.
           Only the radio: the price is ours to write once, and comparing its
           text is what used to fight the converter (see freshPrice). */
        [0, 60, 250].forEach(function (ms) {
          setTimeout(function () {
            if (chosen !== radio || radio.checked) return;
            radios.forEach(function (r) { r.checked = r === radio; });
            apply(radio);
          }, ms);
        });
      }

      if (cfg) {
        // Capture phase, so no other handler can swallow the click first.
        cfg.addEventListener('click', function (e) {
          var btn = e.target.closest('.pbuy__val');
          if (!btn || !cfg.contains(btn)) return;
          e.preventDefault();
          var g = btn.closest('[data-cf-option]');
          var want = selectedValues();
          want[+g.getAttribute('data-cf-option')] = btn.getAttribute('data-value');
          var match = null;
          radios.forEach(function (r) {
            if (!match && optionsOf(r).join('|') === want.join('|')) match = r;
          });
          if (match) choose(match);
        }, true);
      }

      // Everything a chosen variant drives, in one place. The warranty note is
      // the inline varientchange() in cf-product-buy.liquid.
      function apply(radio) {
        sync(radio);
        showOptions(radio);
        leather(radio);
        if (strap) strap(radio);
        if (typeof window.varientchange === 'function') window.varientchange(radio.dataset.title);
      }

      function applyChecked() {
        var checked = root.querySelector('input[name="id"][type="radio"]:checked');
        if (checked) apply(checked);
      }

      radios.forEach(function (radio) {
        radio.addEventListener('change', function () {
          if (radio.checked) apply(radio);
        });
      });

      /* Firefox and Safari restore a radio's checked state on reload and on
         back/forward, while the price and buttons are the server's render of a
         different variant. The click handler then found its match "already
         checked" and skipped the price, so a variant took two clicks. Bring the
         page into line with whatever radio is actually checked, now and on a
         bfcache restore. */
      applyChecked();
      window.addEventListener('pageshow', function (e) {
        if (e.persisted && root.isConnected) applyChecked();
      });
    });
  }

  /* Variants swap the page photography by rule: each rule names the words a
     variant title must contain (Leather; Tyramid; Tyramid + Leather) and a map
     keyed by bundled filename, so every section showing a shot -- and the
     carousel's clones of it -- switches together. The most specific matching
     rule wins, and a variant matching none restores each image's original. */
  var STRAP_WIDTHS = [320, 480, 640, 750, 900, 1100, 1300, 1600];

  function strapSwapper(root) {
    var el = root.querySelector('[data-cf-strap-map]');
    if (!el) return null;
    var rules;
    try { rules = JSON.parse(el.textContent); } catch (e) { return null; }
    if (!Array.isArray(rules)) return null;
    rules.forEach(function (r) {
      r.keywords = (r.keywords || []).map(function (k) { return String(k).toLowerCase(); });
      r.map = r.map || {};
    });

    function fileOf(url) {
      var path = (url || '').split('?')[0];
      return path.slice(path.lastIndexOf('/') + 1);
    }

    function srcsetFor(url) {
      var sep = url.indexOf('?') === -1 ? '?' : '&';
      return STRAP_WIDTHS.map(function (w) { return url + sep + 'width=' + w + ' ' + w + 'w'; }).join(', ');
    }

    function ruleFor(title) {
      var best = null;
      rules.forEach(function (r) {
        if (!r.keywords.length) return;
        var hit = r.keywords.every(function (k) { return title.indexOf(k) !== -1; });
        if (hit && (!best || r.keywords.length > best.keywords.length)) best = r;
      });
      return best;
    }

    // Remembers an image's own src/srcset once, so any rule can be undone.
    function remember(img) {
      if (img.hasAttribute('data-cf-strap')) return;
      img.setAttribute('data-cf-strap', img.getAttribute('src'));
      img.setAttribute('data-cf-strap-srcset', img.getAttribute('srcset') || '');
    }

    function restore(img) {
      if (!img.hasAttribute('data-cf-strap')) return;
      var srcset = img.getAttribute('data-cf-strap-srcset');
      if (srcset) img.srcset = srcset; else img.removeAttribute('srcset');
      img.src = img.getAttribute('data-cf-strap');
      img.removeAttribute('data-cf-strap');
      img.removeAttribute('data-cf-strap-srcset');
    }

    var active;
    return function (radio) {
      var rule = ruleFor((radio.dataset.title || '').toLowerCase());
      if (rule === active) return;
      active = rule;
      var map = rule ? rule.map : {};

      // The hero is a live spin, not a bundled <img>, so it cannot be swapped by
      // filename; it carries its own cut-out, cross-faded in over the spin. A
      // rule's `hero` entry replaces that cut-out; without one it shows its own.
      document.querySelectorAll('[data-cf-hero-strap]').forEach(function (img) {
        var hero = img.closest('.phero');
        if (rule && map.hero) {
          remember(img);
          img.srcset = srcsetFor(map.hero);
          img.src = map.hero;
        } else {
          restore(img);
        }
        if (hero) hero.classList.toggle('phero--strap', !!rule);
        img.setAttribute('aria-hidden', rule ? 'false' : 'true');
      });

      // `-` hides a shot's gallery card (and the rail's clones of it) rather
      // than swapping it: a bracelet shot with no strap version to stand in.
      var reflow = false;
      document.querySelectorAll('img').forEach(function (img) {
        if (img.hasAttribute('data-cf-hero-strap')) return;
        var own = img.getAttribute('data-cf-strap') || img.getAttribute('src');
        // A keyed container (e.g. the specs STRAP tab, "spec-strap") is looked
        // up by its key first, so one spot can swap without every copy of the
        // same file following it; a rule with no entry for the key falls back
        // to the filename.
        var keyEl = img.closest('[data-cf-strap-key]');
        var key = keyEl ? keyEl.getAttribute('data-cf-strap-key') : '';
        var to = (key && map[key]) || map[fileOf(own)];
        var card = img.closest('.gcard');
        if (card) {
          var hide = to === '-';
          if (card.classList.contains('cf-strap-hidden') !== hide) {
            card.classList.toggle('cf-strap-hidden', hide);
            reflow = true;
          }
        }
        if (to === '-') {
          restore(img);
        } else if (to) {
          remember(img);
          img.srcset = srcsetFor(to);
          img.src = to;
        } else {
          restore(img);
        }
      });
      // A card in or out changes the rail's run width, which it measured once.
      if (reflow) document.dispatchEvent(new CustomEvent('cf:reflow'));
    };
  }

  /* --------------------------------------------------- recommendations --- */
  /* Liquid's `recommendations` object is only populated when the section is
     fetched from the Product Recommendations API, so the first render is empty
     and this swaps in the real one. */
  function initRecommendations(scope) {
    scope.querySelectorAll('[data-cf-recommendations]:not([data-cf-init])').forEach(function (host) {
      var url = host.getAttribute('data-url');
      if (!url) return;
      host.setAttribute('data-cf-init', '1');
      fetch(url)
        .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var fresh = parsed.querySelector('[data-cf-recommendations]');
          /* Nothing recommended: keep the collection-based grid that Liquid
             already rendered rather than replacing it with an empty one. */
          if (!fresh || !fresh.querySelector('.vcard')) return;
          host.innerHTML = fresh.innerHTML;
          init(host);
        })
        .catch(function () { /* stays hidden */ });
    });
  }

  /* --------------------------------------------------------------- nav --- */
  /* The floating menu. Closes on Escape, on a click outside, and on any link
     activation; focus moves into the panel on open and back to the trigger on
     close so keyboard users are not stranded. */
  function initNav(scope) {
    scope.querySelectorAll('[data-cf-nav]:not([data-cf-init])').forEach(function (root) {
      var btn = root.querySelector('[data-cf-nav-toggle]');
      var panel = root.querySelector('.cf-nav__panel');
      var scrim = root.querySelector('[data-cf-nav-scrim]');
      if (!btn || !panel) return;
      root.setAttribute('data-cf-init', '1');

      function setOpen(open) {
        root.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        /* Only lock scrolling once the panel is tall enough to matter; on a
           short menu the lock is more disruptive than the scroll. */
        if (open) {
          var first = panel.querySelector('a');
          if (first) first.focus({ preventScroll: true });
        } else {
          btn.focus({ preventScroll: true });
        }
      }

      btn.addEventListener('click', function () {
        setOpen(!root.classList.contains('is-open'));
      });

      if (scrim) scrim.addEventListener('click', function () { setOpen(false); });

      /* Navigating away closes it; so does Escape from anywhere inside. */
      panel.addEventListener('click', function (e) {
        if (e.target.closest('a')) root.classList.remove('is-open');
      });

      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape' || !root.isConnected) return;
        if (root.classList.contains('is-open')) setOpen(false);
      });

      document.addEventListener('click', function (e) {
        if (!root.classList.contains('is-open')) return;
        if (!root.contains(e.target)) root.classList.remove('is-open');
      });
    });
  }

  /* ----------------------------------------------------- smooth scroll -- */
  /* Lenis, if the theme setting loaded it. Started once for the document, not
     per section, and never when the visitor has asked for reduced motion —
     hijacking the scroll is exactly what that preference is about.

     Anchor links are routed through Lenis so in-page jumps (the footer's FAQ
     link, for instance) still land, and any element marked
     [data-lenis-prevent] keeps its own native scrolling. */
  var lenis = null;
  var lenisFrame = null;

  /* Horizon does not scroll the window on desktop. At >=990px base.css gives
     html and body `height:100dvh; overflow:hidden` and makes .page-wrapper the
     scroll container (`overflow-y:auto`); below that breakpoint the document
     scrolls normally. Its own assets/scroll-container.js documents the same
     split. Lenis defaults to `wrapper: window`, so on desktop it was attached
     to something that never scrolls — which is why it did nothing at all. */
  function scrollWrapper() {
    var pw = document.querySelector('.page-wrapper');
    if (!pw) return window;
    var oy = getComputedStyle(pw).overflowY;
    return (oy === 'auto' || oy === 'scroll') ? pw : window;
  }

  function initSmoothScroll() {
    if (reduced || !window.Lenis) return;

    var wrapper = scrollWrapper();
    var dur = parseFloat(document.documentElement.getAttribute('data-cf-scroll-duration'));

    lenis = new window.Lenis({
      wrapper: wrapper,
      /* for an element wrapper the content box is the wrapper itself, and
         Lenis only reads it to measure the scrollable length */
      content: wrapper === window ? document.documentElement : wrapper,
      duration: isNaN(dur) ? 1.1 : dur,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: true
    });
    lenis.__cfWrapper = wrapper;

    function frame(time) {
      if (lenis) lenis.raf(time);
      lenisFrame = requestAnimationFrame(frame);
    }
    lenisFrame = requestAnimationFrame(frame);

    /* Crossing 990px swaps which element scrolls, so rebuild against the new
       one rather than leaving Lenis bound to the old container. */
    var rt;
    window.addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(function () {
        if (!lenis || scrollWrapper() === lenis.__cfWrapper) return;
        lenis.destroy();
        if (lenisFrame) cancelAnimationFrame(lenisFrame);
        lenis = null;
        initSmoothScroll();
      }, 200);
    });

    /* Native anchor jumps do not move Lenis's own scroll position. */
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href^="#"]');
      if (!a) return;
      var id = a.getAttribute('href');
      if (!id || id === '#') return;
      var target = document.querySelector(id);
      if (!target) return;
      e.preventDefault();
      lenis.scrollTo(target, { offset: -24 });
    });

    /* The theme editor scrolls to a section on select; let it. */
    if (editing) {
      document.addEventListener('shopify:section:select', function (e) {
        lenis.scrollTo(e.target, { immediate: true });
      });
    }
  }

  /* ------------------------------------------------- adaptive nav ink --- */
  /* Reads the --cf-ground of whichever section sits behind the fixed trigger
     and flips the control's colours to suit. elementFromPoint is no use here:
     the button is itself the topmost element at that point. */
  function initNavInvert(scope) {
    scope.querySelectorAll('.cf-nav--auto:not([data-cf-invert])').forEach(function (nav) {
      var btn = nav.querySelector('[data-cf-nav-toggle]');
      if (!btn) return;
      nav.setAttribute('data-cf-invert', '1');

      /* The design element inside each section wrapper is what carries the
         background, so probe those rather than the wrappers. */
      function grounds() {
        /* Any section may declare --cf-ground (the Olympus II product
           sections do); one that does not reads as dark, as before. */
        return document.querySelectorAll('#MainContent > .cf-section > *, #MainContent > .shopify-section > *');
      }

      var pending = false;
      function measure() {
        pending = false;
        var r = btn.getBoundingClientRect();
        var y = r.top + r.height / 2;
        var found = null;
        grounds().forEach(function (el) {
          if (found) return;
          var b = el.getBoundingClientRect();
          if (y >= b.top && y <= b.bottom) found = el;
        });
        var ground = 'dark';
        if (found) {
          var v = getComputedStyle(found).getPropertyValue('--cf-ground').trim();
          if (v) ground = v;
        }
        nav.classList.toggle('cf-nav--on-light', ground === 'light');
        nav.classList.toggle('cf-nav--on-dark', ground !== 'light');
      }

      function schedule() {
        if (pending) return;
        pending = true;
        requestAnimationFrame(measure);
      }

      measure();

      /* Horizon scrolls .page-wrapper on desktop and the document below that,
         so listen on whichever one is actually scrolling. Lenis still moves
         real scrollTop, so these fire while it is running. */
      var scroller = (typeof scrollWrapper === 'function') ? scrollWrapper() : window;
      (scroller === window ? window : scroller)
        .addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);
    });
  }

  /* ------------------------------------------------------------- init --- */
  function init(scope) {
    scope = scope || document;
    /* Each module runs on its own: one throwing (a missing element, an app
       block changing the markup) must not leave the ones after it unbound --
       initBuy sits near the end, and without it variant prices never update. */
    [
      initStagger, initReveal, initLoops, initActiveFilm, initCarousels,
      initZoom, initVideoCursor, initExpandCursor, initTabs, initAccordion,
      initVideo, initFilmSound, initDrift, initCursorNav, initLoopNav,
      initNav, initNavInvert, initBuy, initRecommendations
    ].forEach(function (fn) {
      try { fn(scope); } catch (e) { console.error('[cf] ' + fn.name + ' failed', e); }
    });
  }

  /* `lenis` is a getter, not the instance: it is null until initSmoothScroll()
     runs on the next line, and it is destroyed and rebuilt whenever the viewport
     crosses 990px and the scrolling element changes underneath it. Anything that
     drives the scroll itself — the 3D panel section's magnetic snap, for one —
     must go through Lenis rather than write scrollTop behind its back, or the
     two disagree about where the page is and the next wheel event snaps back. */
  window.CarterFarrago = { init: init, lenis: function () { return lenis; } };
  initSmoothScroll();
  init(document);

  /* Shopify replaces a section's DOM wholesale on every settings change; the
     guards above make re-initialising the new node safe and local. */
  if (editing) {
    document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  }
})();
